import { Button, Icon, IconTile } from "@iec-id/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AuthCard, AuthHeading } from "@/components/signin/auth-card";
import { useSigninT } from "@/components/signin/signin-i18n";
import { parseAuthSearch, resolveService, safeExternalUrl } from "@/lib/auth-flow";

export const Route = createFileRoute("/error")({
	validateSearch: parseAuthSearch,
	component: ErrorScreen,
});

/** Коды, для которых у нас есть человеческое объяснение. */
const KNOWN_REASONS = [
	"unknown_client",
	"invalid_redirect_uri",
	"expired_link",
	"access_denied",
	"server_error",
	/**
	 * Ссылка из письма СТАРОГО SSO: подтверждение почты (`/callback?token=`),
	 * сброс пароля (`/reset-password`), заглушка `/login`. На Э5
	 * `sso.iec.exchange` начнёт обслуживаться нами, и такие письма — уже
	 * отправленные, живущие до семи суток — приедут сюда. Показать им «Not
	 * Found» роутера значит встретить человека ошибкой ровно в тот момент,
	 * когда его попросили что-то сделать.
	 */
	"legacy_link",
] as const;

type Reason = (typeof KNOWN_REASONS)[number] | "unknown";

/**
 * OAuth-коды из RFC 6749 приходят в нескольких написаниях, а объяснений у нас
 * пять. Сводим синонимы к своим ключам: человеку всё равно, назвал сервер это
 * `invalid_client` или `unauthorized_client`, — ему нужно знать, что делать.
 */
const SYNONYMS: Record<string, Reason> = {
	invalid_client: "unknown_client",
	unauthorized_client: "unknown_client",
	unknown_client: "unknown_client",
	invalid_redirect_uri: "invalid_redirect_uri",
	redirect_uri_mismatch: "invalid_redirect_uri",
	invalid_grant: "expired_link",
	invalid_token: "expired_link",
	expired_link: "expired_link",
	expired_token: "expired_link",
	access_denied: "access_denied",
	server_error: "server_error",
	temporarily_unavailable: "server_error",
	legacy_link: "legacy_link",
};

function reasonOf(raw: string | undefined): Reason {
	if (!raw) return "unknown";
	return SYNONYMS[raw.toLowerCase()] ?? "unknown";
}

/** Код для поддержки. Всё, что не похоже на код, не показываем вовсе. */
function supportCode(raw: string | undefined): string | undefined {
	if (!raw || !/^[a-z0-9_.-]{1,64}$/i.test(raw)) return undefined;
	return raw.toLowerCase();
}

/**
 * Понятный отказ (макет 4a, ветка ошибок).
 *
 * `error_description` из адресной строки сюда не попадает намеренно: параметр
 * приходит от кого угодно, а страница живёт на `sso.iec.exchange` — чужой текст
 * на домене входа это готовый фишинг. Показываем свой текст и голый код.
 */
function ErrorScreen() {
	const search = Route.useSearch();
	const navigate = useNavigate();
	const t = useSigninT();
	const service = resolveService(search);

	const reason = reasonOf(search.error);
	// Устаревшая ссылка — не сбой: показывать «код для поддержки» здесь значит
	// предлагать человеку пожаловаться на то, что работает как задумано.
	const code = reason === "legacy_link" ? undefined : supportCode(search.error);
	// Возврат в сервис предлагаем только на проверенный адрес экосистемы.
	const serviceUrl = service ? safeExternalUrl(service.url) : undefined;

	return (
		<AuthCard>
			<IconTile size={48} className="bg-danger-soft text-danger">
				<Icon name="TriangleAlert" size={24} />
			</IconTile>

			<AuthHeading title={t(`error.${reason}.title`)} subtitle={t(`error.${reason}.description`)} />

			{code ? <p className="t-meta font-mono text-fg-muted">{code}</p> : null}

			<div className="flex flex-col gap-2.5">
				<Button
					variant="primary"
					fullWidth
					onClick={() => {
						// Ошибку из адресной строки уносим с собой: иначе экран входа
						// откроется и тут же вернёт человека сюда.
						const { error: _error, error_description: _description, ...rest } = search;
						void navigate({ to: "/", search: rest, replace: true });
					}}
				>
					{t("error.backToSignIn")}
				</Button>
				{service && serviceUrl ? (
					<Button variant="second" fullWidth onClick={() => window.location.assign(serviceUrl)}>
						{t("error.toService", { service: service.name })}
					</Button>
				) : null}
			</div>
		</AuthCard>
	);
}
